import type { FormattedAddress } from '@ixigo/cab-core/location';
import { createTracker } from '@/lib/analytics/create-tracker';
import { globalTracker } from '@/utils/track';
import { getSearchFormData } from './store';

export interface SearchTrackingData {
	/**
	 * defaults to airport for now
	 */
	tripType?: 'airport' | 'outstation' | 'rental';
	pickup: FormattedAddress;
	drop: FormattedAddress;

	/**
	 * pickup date time string or timestamp
	 * used to derive apd and extract date & time
	 * later we need to consider time zone as well bcoz pickup time zone could be different from booking device time zone
	 */
	pickupTime: string | number;
}

export const searchTracker = /*#__PURE__*/ createTracker({
	track: globalTracker.track,
	getCommonAttributes: () => {
		const { pickup, drop, dateTime } = getSearchFormData();
		return pickup && drop && dateTime
			? getCommonSearchAttributes({
					pickup,
					drop,
					pickupTime: dateTime,
				})
			: {};
	},
	trackMap: {
		search: () => ({
			eventName: 'Cab Search',
		}),
	},
});

export function getCommonSearchAttributes(data: SearchTrackingData) {
	// dervice apd(difference in departure date and booking date. consider booking date as today) from pickup time
	// although for APD purchase date is required, we are using today's date as purchase date for now
	// departure date in format of 02/06/2025
	// departure time in format of 18:15
	const pickupDate = new Date(data.pickupTime);
	const apd = differenceInDays(pickupDate, new Date());
	return {
		Type: data.tripType || 'airport',
		Origin: data.pickup.formattedAddress,
		Destination: data.drop.formattedAddress,
		APD: apd,
		'Departure Date': formatDate(pickupDate),
		'Departure Time': formatTime(pickupDate),
	};
}

function differenceInDays(date1: Date, date2: Date) {
	return Math.floor(
		(date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24),
	);
}

function formatDate(date: Date) {
	return date.toLocaleDateString('en-US', {
		month: '2-digit',
		day: '2-digit',
		year: 'numeric',
	});
}

function formatTime(date: Date) {
	return date.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});
}
